import { useEffect, useRef, useState } from 'react';

import { useThrottledCallback } from '@/hooks';
import { AnalyticsService, EVENT_CONSTANTS } from '@/services';
import { Referrer } from '@/types';

const SCROLL_ENDZONE_DISTANCE = 40;

export const useCarouselScroll = ({
  itemWidth,
  uKey,
  referrer = {},
}: {
  itemWidth: number;
  uKey: string;
  referrer: Referrer;
}) => {
  const containerId = `${uKey}-Carousel`;

  const scrollAmount = useRef(0);

  const [isNearBeginning, setIsNearBeginning] = useState(true);
  const [isNearEnd, setIsNearEnd] = useState(false);

  const trackUserScroll = useThrottledCallback(() => {
    const container = document.getElementById(containerId);
    if (container) {
      const maxScroll = container.scrollWidth - container.clientWidth;
      setIsNearBeginning(container.scrollLeft < SCROLL_ENDZONE_DISTANCE);
      setIsNearEnd(maxScroll - container.scrollLeft < SCROLL_ENDZONE_DISTANCE);

      scrollAmount.current = container.scrollLeft;
    }
  }, 300);

  useEffect(() => {
    const container = document.getElementById(containerId);

    container?.addEventListener('scroll', trackUserScroll);

    return () => {
      container?.removeEventListener('scroll', trackUserScroll);
    };
  }, [containerId, trackUserScroll]);

  const scroll = (toLeft: boolean) => {
    const lastChild = document.getElementById(containerId)
      ?.lastChild as HTMLElement;
    const scrollMax = lastChild?.clientWidth - itemWidth;

    document.getElementById(containerId)?.scrollTo({
      top: 0,
      left: (scrollAmount.current += toLeft ? -itemWidth : itemWidth),
      behavior: 'smooth',
    });

    AnalyticsService.carouselScroll({
      direction: toLeft ? 'left' : 'right',
      location: {
        ...referrer,
        button: toLeft
          ? EVENT_CONSTANTS.BUTTON.SCROLL_LEFT
          : EVENT_CONSTANTS.BUTTON.SCROLL_RIGHT,
      },
      referrer: { ...referrer, carousel: uKey },
    });

    if (scrollAmount.current > scrollMax) {
      scrollAmount.current = scrollMax;
    } else if (scrollAmount.current < 0) {
      scrollAmount.current = 0;
    }
  };

  return {
    isNearBeginning,
    isNearEnd,
    scroll,
    containerId,
  };
};
